/**
 * Gefaehrdungsfaktoren fuer die Gefaehrdungsbeurteilung (SiFa-Modul GBU).
 *
 * ─── Warum der Katalog im Repo liegt und nicht in der Datenbank ───────────
 * Begruendung wie beim Gewerkekatalog (`koordinierung/gewerkeKatalog.ts`):
 * eine freigegebene GBU haelt fest, mit welcher Katalogfassung sie erstellt
 * wurde (`GbuFassungInhalt.kataloge.faktoren`). Laege der Katalog in einer
 * Tabelle und wuerde dort fortgeschrieben, aenderte sich rueckwirkend, was
 * eine unterschriebene Fassung ueber ihre Faktoren sagt — und genau das
 * zerstoert den Beweiswert. Eine neue Fassung erhoeht
 * `GEFAEHRDUNGSFAKTOREN_FASSUNG`; die eingefrorene Fassung traegt die Labels
 * ohnehin aufgeloest (gbuLogic.baueFassungsInhalt).
 *
 * ─── Die Codes sind unveraenderlich ──────────────────────────────────────
 * Die Codes stehen in `sifa_gbu_gefaehrdungen.faktor` und in jeder
 * eingefrorenen Fassung. Ein Code wird nie umbenannt und nie neu vergeben;
 * die Menge ist der Typ `GefaehrdungsFaktor` in gbuTypes.ts.
 *
 * ─── Inhalt ──────────────────────────────────────────────────────────────
 * Die 11 Gruppen der BAuA-Systematik „Gefaehrdungsfaktoren" (Ratgeber zur
 * Gefaehrdungsbeurteilung). Die `beispiele` sind die typischen Unterfaktoren
 * der Gruppe, ergaenzt um Baustellen-Beispiele in Klammern. Sie sind
 * Ausfuellhilfe und Grundlage der Vollstaendigkeitspruefung (KI K2), keine
 * abschliessende Liste. Gruppe 10 steht ausdruecklich drin: § 5 Abs. 3 Nr. 6
 * ArbSchG nennt die psychische Belastung als eigenen Gefaehrdungsgrund.
 *
 * ─── Beweiswert ──────────────────────────────────────────────────────────
 * Fassung 1, vom Betreiber fachlich zu pruefen, bevor sie aktiv wird
 * (docs/sifa-gbu-plan.md §5).
 *
 * ─── Quellen ─────────────────────────────────────────────────────────────
 * BAuA, Ratgeber zur Gefaehrdungsbeurteilung, Gefaehrdungsfaktoren 1–11 ·
 * ArbSchG §§ 4, 5.
 */

import type { GefaehrdungsFaktor } from '../gbuTypes';

/** Fassung des Katalogs. Bei jeder inhaltlichen Aenderung erhoehen. */
export const GEFAEHRDUNGSFAKTOREN_FASSUNG = 1;

export interface GefaehrdungsfaktorEintrag {
  code: GefaehrdungsFaktor;
  /** Nummer der Gruppe in der BAuA-Systematik (1–11). */
  nr: number;
  label: string;
  /** Kurzbeschreibung der Gruppe. */
  beschreibung: string;
  /** Typische Unterfaktoren — Ausfuellhilfe, nicht abschliessend. */
  beispiele: string[];
}

export const GEFAEHRDUNGSFAKTOREN: ReadonlyArray<GefaehrdungsfaktorEintrag> = [
  {
    code: 'mechanisch',
    nr: 1,
    label: 'Mechanische Gefährdungen',
    beschreibung: 'Verletzungen durch Bewegung, Kontakt oder Sturz.',
    beispiele: [
      'Ungeschützt bewegte Maschinenteile (Quetsch-, Scher-, Einzugsstellen)',
      'Teile mit gefährlichen Oberflächen (Ecken, Kanten, Spitzen, Bewehrungsanschlüsse)',
      'Bewegte Transportmittel und Arbeitsmittel (Bagger, Stapler, Fahrzeuge)',
      'Unkontrolliert bewegte Teile (schwebende Lasten, umstürzende Teile, Verschüttung)',
      'Sturz auf der Ebene, Ausrutschen, Stolpern, Umknicken',
      'Absturz (Gerüste, Leitern, Dachkanten, Öffnungen, Baugruben)',
    ],
  },
  {
    code: 'elektrisch',
    nr: 2,
    label: 'Elektrische Gefährdungen',
    beschreibung: 'Gefährdung durch elektrischen Strom und Ladung.',
    beispiele: [
      'Elektrischer Schlag (Baustromverteiler, beschädigte Leitungen, Freileitungen)',
      'Lichtbögen',
      'Elektrostatische Aufladung',
    ],
  },
  {
    code: 'gefahrstoffe',
    nr: 3,
    label: 'Gefahrstoffe',
    beschreibung: 'Gesundheitsgefährdung durch Stoffe und Gemische nach GefStoffV.',
    beispiele: [
      'Hautkontakt mit Gefahrstoffen (Zement, Frischbeton, Schalöl, Feuchtarbeit)',
      'Einatmen von Gefahrstoffen (Gase, Dämpfe, Nebel, Stäube, Rauche)',
      'Verschlucken von Gefahrstoffen',
      'Physikalisch-chemische Gefährdungen (unkontrollierte chemische Reaktionen)',
    ],
  },
  {
    code: 'biologisch',
    nr: 4,
    label: 'Biologische Arbeitsstoffe',
    beschreibung: 'Gefährdung durch Biostoffe nach BioStoffV.',
    beispiele: [
      'Infektionsgefährdung durch Bakterien, Viren, Pilze (Abwasser, Fäkalien, Zecken)',
      'Sensibilisierende und toxische Wirkung von Mikroorganismen (Schimmelpilze, Taubenkot)',
    ],
  },
  {
    code: 'brand_explosion',
    nr: 5,
    label: 'Brand- und Explosionsgefährdungen',
    beschreibung: 'Entstehung und Ausbreitung von Bränden und Explosionen.',
    beispiele: [
      'Brennbare Feststoffe, Flüssigkeiten und Gase (Kraftstoffe, Flüssiggas)',
      'Explosionsfähige Atmosphäre (Lösemitteldämpfe, Stäube)',
      'Explosivstoffe',
      'Heißarbeiten (Schweißen, Trennschleifen, Brennschneiden)',
    ],
  },
  {
    code: 'thermisch',
    nr: 6,
    label: 'Thermische Gefährdungen',
    beschreibung: 'Verbrennungen und Erfrierungen durch Kontakt.',
    beispiele: [
      'Heiße Medien und Oberflächen (Bitumen, Gussasphalt, Schweißteile)',
      'Kalte Medien und Oberflächen',
    ],
  },
  {
    code: 'physikalisch',
    nr: 7,
    label: 'Spezielle physikalische Einwirkungen',
    beschreibung: 'Lärm, Vibration, Strahlung und Druck.',
    beispiele: [
      'Lärm (Stemmen, Trennschleifen, Rüttler, Bolzensetzgeräte)',
      'Ultraschall, Infraschall',
      'Ganzkörper-Vibration (Baumaschinen, Rüttelplatten)',
      'Hand-Arm-Vibration (Bohr- und Abbruchhämmer, Rüttelflaschen)',
      'Optische Strahlung (UV durch Sonne oder Schweißen, Infrarot, Laser)',
      'Ionisierende Strahlung',
      'Elektromagnetische Felder',
      'Unter- oder Überdruck',
    ],
  },
  {
    code: 'arbeitsumgebung',
    nr: 8,
    label: 'Arbeitsumgebungsbedingungen',
    beschreibung: 'Klima, Licht, Raum und Wege am Arbeitsplatz.',
    beispiele: [
      'Klima (Hitze, Kälte, Nässe, unzureichende Lüftung, Arbeiten im Freien)',
      'Beleuchtung, Licht',
      'Ersticken (sauerstoffarme Atmosphäre in Schächten und Behältern), Ertrinken',
      'Unzureichende Flucht- und Verkehrswege, fehlende Sicherheitskennzeichnung',
      'Unzureichende Bewegungsfläche, ungünstige Anordnung des Arbeitsplatzes',
      'Unzureichende Pausen- und Sanitärräume',
    ],
  },
  {
    code: 'physische_belastung',
    nr: 9,
    label: 'Physische Belastung/Arbeitsschwere',
    beschreibung: 'Belastung des Muskel-Skelett-Systems.',
    beispiele: [
      'Schwere dynamische Arbeit, manuelle Handhabung von Lasten (Steine, Schalungselemente, Säcke)',
      'Einseitige dynamische Arbeit, häufig wiederholte Bewegungen',
      'Haltungsarbeit (Zwangshaltung: Knien, Bücken, Arbeiten über Kopf), Haltearbeit',
      'Kombination aus statischer und dynamischer Arbeit',
    ],
  },
  {
    code: 'psychisch',
    nr: 10,
    label: 'Psychische Faktoren',
    beschreibung: 'Psychische Belastung aus Aufgabe, Organisation, sozialen Beziehungen und Umgebung.',
    beispiele: [
      'Ungenügend gestaltete Arbeitsaufgabe (Über- oder Unterforderung, fehlender Handlungsspielraum)',
      'Ungenügend gestaltete Arbeitsorganisation (Zeitdruck, lange oder wechselnde Arbeitszeiten, Termindruck)',
      'Ungenügend gestaltete soziale Bedingungen (Konflikte, fehlende Unterstützung, Führungsverhalten)',
      'Ungenügend gestaltete Arbeitsplatz- und Umgebungsbedingungen (Lärm, Klima, Enge, Alleinarbeit)',
    ],
  },
  {
    code: 'sonstige',
    nr: 11,
    label: 'Sonstige Gefährdungen',
    beschreibung: 'Gefährdungen, die keiner anderen Gruppe zuzuordnen sind.',
    beispiele: [
      'Durch Menschen (Übergriffe, Überfall)',
      'Durch Tiere (Bisse, Stiche)',
      'Durch Pflanzen und pflanzliche Produkte (sensibilisierende und toxische Wirkung)',
    ],
  },
];

const NACH_CODE = new Map<string, GefaehrdungsfaktorEintrag>(GEFAEHRDUNGSFAKTOREN.map((f) => [f.code, f]));

/** Ist `code` ein Faktor-Code des Katalogs? (Der KI-Leser verwirft alles andere.) */
export const istFaktorCode = (code: string | null | undefined): code is GefaehrdungsFaktor =>
  typeof code === 'string' && NACH_CODE.has(code);

/** Katalogeintrag zum Code, sonst null. */
export const faktorEintrag = (code: string | null | undefined): GefaehrdungsfaktorEintrag | null =>
  (typeof code === 'string' && NACH_CODE.get(code)) || null;

/**
 * Anzeigename zum Code. Ein unbekannter Code kommt unveraendert zurueck statt
 * leer: eine Zeile mit fremdem Code soll im PDF auffallen, nicht verschwinden.
 */
export const faktorLabel = (code: string | null | undefined): string =>
  faktorEintrag(code)?.label ?? (code ?? '');
